import type { Auftrag, AuftragsStatus, AuftragsTyp } from '@awf/shared';
import type { LinienSymbolName } from './ui/linien-symbol.js';

/**
 * EINE Stelle je Auftragstyp. Vorher stand jeder Typ an sieben Stellen im Code (Spec C,
 * Abschnitt 3). Seit 2c Teil 2 ist PDP der vierte Typ; ein neuer Typ ist ein
 * Eintrag hier plus `FELDSAETZE`, und der Typcheck erzwingt beide, sobald `AuftragsTyp` in
 * @awf/shared waechst.
 */
export interface Schritt {
  status: AuftragsStatus;
  titel: string;
  /** Ein Satz fuer die Detailkarte, solange dieser Schritt laeuft. */
  hinweis: string;
}

/**
 * `seite`: Freigabe auf /auftraege/:id/freigabe. `keine`: die Kette kennt gar kein Warten.
 * `extern` (Freigabe nur per Mail-Formular) gibt es seit 2026-10-13 nicht mehr: Auch Bilder
 * werden hier freigegeben.
 */
export type FreigabeArt = 'seite' | 'keine';

export interface TypBeschreibung {
  text: string;
  /**
   * Titel der Kachel auf "Neuer Auftrag", wo er sagen soll, was der Auftrag TUT (Rueckmeldung
   * 2026-10-14), und derselbe Name in jeder Typ-Auswahl (Rueckmeldung 2026-10-16), damit man
   * dort wiederfindet, was man angelegt hat. In Tabellenkoepfen und Fliesstext steht der kurze
   * `text`: Dort waere der lange Name zu breit. Ohne Angabe gilt `text`.
   */
  kachelTitel?: string;
  erklaerung: string;
  symbol: LinienSymbolName;
  /**
   * Zeitbedarf in Worten, auf jeder Typkarte und im Ablauf. Aus Messungen abgeleitet; wo noch
   * keine vorliegt, steht das am Eintrag (eine geratene Dauer waere eine falsche Zusage).
   */
  dauer: string;
  schritte: readonly Schritt[];
  /**
   * Ein Pfad, der nur auf Wunsch laeuft, im Ablauf neben dem Formular gestrichelt (Rueckmeldung
   * 2026-10-13: die Analyse kann einen Ratgeber auch optimieren, samt Freigabe). Nur zur
   * Anzeige; die Schrittleiste der Fortschrittsseite folgt weiter `schritte`.
   */
  aufWunsch?: { nach: AuftragsStatus; titel: string; schritte: readonly Schritt[] };
  freigabe: FreigabeArt;
}

export const AUFTRAGSTYPEN = {
  ratgeber: {
    text: 'Ratgeber',
    kachelTitel: 'Ratgeber-Erstellung',
    erklaerung: 'Erzeugt aus einem Briefing einen vollständigen, SEO-optimierten Ratgeber.',
    symbol: 'file',
    // AP3-Messungen und HAENGT_NACH_MS: rund 20 Minuten bis zur Freigabe.
    dauer: 'ca. 20 Minuten bis zur Freigabe',
    freigabe: 'seite',
    schritte: [
      {
        status: 'angelegt',
        titel: 'Angelegt',
        hinweis: 'Der Auftrag ist angenommen und startet gleich.',
      },
      {
        status: 'recherche',
        titel: 'Recherche',
        hinweis: 'Produktdaten, Keywords, Wettbewerb und Fragen werden ausgewertet.',
      },
      {
        status: 'entwurf',
        titel: 'Entwurf',
        hinweis: 'Der Text wird geschrieben und geprüft, die Bilder entstehen.',
      },
      {
        status: 'wartet_auf_freigabe',
        titel: 'Freigabe',
        hinweis:
          'Der Entwurf liegt bereit. Prüfen Sie ihn und geben Sie ihn frei, oder bestellen Sie eine Korrekturrunde.',
      },
      {
        status: 'finalisierung',
        titel: 'Finalisierung',
        hinweis: 'Links werden eingebaut, Text und Bilder werden abgelegt.',
      },
      { status: 'fertig', titel: 'Fertig', hinweis: 'Der Ratgeber ist fertig.' },
    ],
  },
  bilder: {
    text: 'Bilder',
    kachelTitel: 'Bild-Erstellung',
    erklaerung: 'Erzeugt markenkonforme Bilder zu einem Thema, mit Alternativtexten.',
    symbol: 'image',
    // VORLAEUFIG, nicht gemessen (2026-10-13): Auftrag "jede Karte mit Zeitbedarf", aber fuer
    // Bilder liegt noch kein gemessener Lauf vor. Nach dem ersten Live-Lauf ersetzen.
    dauer: 'ca. 5 Minuten bis zur Freigabe',
    // Seit 2026-10-13 auf derselben Freigabeseite wie der Ratgeber, nur ohne Text.
    freigabe: 'seite',
    schritte: [
      {
        status: 'angelegt',
        titel: 'Angelegt',
        hinweis: 'Der Auftrag ist angenommen und startet gleich.',
      },
      {
        status: 'entwurf',
        titel: 'Entwurf',
        hinweis: 'Die Bilder werden erzeugt und auf Markenkonformität geprüft.',
      },
      {
        status: 'wartet_auf_freigabe',
        titel: 'Freigabe',
        hinweis:
          'Die Bilder liegen bereit. Prüfen Sie sie und geben Sie sie frei, oder bestellen Sie eine Änderung.',
      },
      {
        status: 'finalisierung',
        titel: 'Finalisierung',
        hinweis: 'Die freigegebenen Bilder werden abgelegt.',
      },
      { status: 'fertig', titel: 'Fertig', hinweis: 'Die Bilder sind fertig.' },
    ],
  },
  analyse: {
    text: 'Ratgeber-Analyse',
    erklaerung:
      'Prüft eine bestehende Ratgeberseite oder ein ganzes Verzeichnis auf Optimierungsbedarf und optimiert sie auf Wunsch.',
    symbol: 'search',
    // Gemessen: ein Verzeichnislauf dauerte 92 Minuten, eine einzelne URL einen Bruchteil.
    dauer: 'wenige Minuten je Seite, bis ca. 1,5 Stunden je Verzeichnis',
    freigabe: 'keine',
    // Nach der Entscheidung je Seite laeuft die Optimierung als eigener Ratgeber-Auftrag
    // (PR #71); die Ablaufkarte zeigt diesen Weg gestrichelt, die Analyse selbst endet "Fertig".
    aufWunsch: {
      nach: 'wartet_auf_entscheidung',
      titel: 'Optimierung auf Wunsch',
      schritte: [
        {
          status: 'entwurf',
          titel: 'Entwurf',
          hinweis: 'Der überarbeitete Ratgeber wird geschrieben und geprüft.',
        },
        {
          status: 'wartet_auf_freigabe',
          titel: 'Freigabe',
          hinweis: 'Der Entwurf liegt bereit. Prüfen Sie ihn und geben Sie ihn frei.',
        },
        {
          status: 'finalisierung',
          titel: 'Finalisierung',
          hinweis: 'Text und Bilder werden abgelegt.',
        },
      ],
    },
    schritte: [
      {
        status: 'angelegt',
        titel: 'Angelegt',
        hinweis: 'Der Auftrag ist angenommen und startet gleich.',
      },
      {
        status: 'recherche',
        titel: 'Analyse',
        hinweis: 'Die Seiten werden gelesen und auf Optimierungsbedarf geprüft.',
      },
      {
        status: 'wartet_auf_entscheidung',
        titel: 'Entscheidung',
        hinweis: 'Die Analyse ist fertig. Entscheiden Sie je Seite, ob sie optimiert werden soll.',
      },
      {
        status: 'fertig',
        titel: 'Fertig',
        hinweis:
          'Die Analyse ist abgeschlossen. Die Seiten und Ihre Entscheidungen stehen im Auftrag.',
      },
    ],
  },
  pdp: {
    text: 'Produktseite',
    // Geschuetztes Leerzeichen und nicht trennender Bindestrich: "-Optimierung" bleibt beisammen.
    kachelTitel: 'Produktseiten-Analyse und\u00a0\u2011Optimierung',
    erklaerung: 'Analysiert eine Produktdetailseite und optimiert sie auf Wunsch.',
    symbol: 'globe',
    // Gemessen an zwei Laeufen: Analyse bis zur Entscheidung 11 Minuten, Entwurf danach 11 Minuten.
    dauer: 'ca. 10 Minuten bis zu Ihrer Entscheidung',
    // Stufe c (Spec 2026-10-12): Freigabe auf derselben Seite wie der Ratgeber, nicht mehr per Mail.
    freigabe: 'seite',
    schritte: [
      {
        status: 'angelegt',
        titel: 'Angelegt',
        hinweis: 'Der Auftrag ist angenommen und startet gleich.',
      },
      {
        status: 'recherche',
        titel: 'Analyse',
        hinweis: 'Die Produktseite wird gelesen und mit Suchdaten und Wettbewerb verglichen.',
      },
      {
        status: 'wartet_auf_entscheidung',
        titel: 'Entscheidung',
        hinweis: 'Die Analyse ist fertig. Entscheiden Sie, ob die Seite optimiert werden soll.',
      },
      {
        status: 'entwurf',
        titel: 'Entwurf',
        hinweis: 'Der neue Seitentext wird geschrieben und geprüft, die Bilder entstehen.',
      },
      {
        status: 'wartet_auf_freigabe',
        titel: 'Freigabe',
        hinweis:
          'Der Entwurf liegt bereit. Prüfen Sie ihn und geben Sie ihn frei, oder bestellen Sie eine Korrekturrunde.',
      },
      {
        status: 'finalisierung',
        titel: 'Finalisierung',
        hinweis: 'Text und Bilder werden abgelegt.',
      },
      { status: 'fertig', titel: 'Fertig', hinweis: 'Die Produktseite ist fertig.' },
    ],
  },
} as const satisfies Record<AuftragsTyp, TypBeschreibung>;

// Ratgeber und Ratgeber-Analyse nebeneinander (Rueckmeldung 2026-10-13): beide arbeiten an
// Ratgebern, auf der Typwahl stehen sie deshalb in einer Zeile.
/** Der Name aus "Neuer Auftrag", fuer jede Typ-Auswahl (siehe `kachelTitel`). */
export function typName(typ: AuftragsTyp): string {
  const t: TypBeschreibung = AUFTRAGSTYPEN[typ];
  return t.kachelTitel ?? t.text;
}

export const TYP_REIHENFOLGE: readonly AuftragsTyp[] = ['ratgeber', 'analyse', 'pdp', 'bilder'];

/** Die Adresse ist Eingabe von aussen; `in` wuerde auch `toString` aus dem Prototyp finden. */
export function istAuftragsTyp(roh: string): roh is AuftragsTyp {
  return (TYP_REIHENFOLGE as readonly string[]).includes(roh);
}

type Register = typeof AUFTRAGSTYPEN;

/** Typen, auf die ein Mensch warten kann -- daraus leitet die Seitenleiste ihre Zaehler ab. */
export type TypMitFreigabe = {
  [T in AuftragsTyp]: Register[T]['freigabe'] extends 'keine' ? never : T;
}[AuftragsTyp];

export function hatFreigabe(typ: AuftragsTyp): typ is TypMitFreigabe {
  return AUFTRAGSTYPEN[typ].freigabe !== 'keine';
}

export const TYPEN_MIT_FREIGABE: readonly TypMitFreigabe[] = TYP_REIHENFOLGE.filter(hatFreigabe);

/** Fuehrt "Entwurf pruefen" zur Freigabeseite? Nur, wo `GET /runs/:id/text` antwortet. */
export function hatFreigabeSeite(auftrag: Pick<Auftrag, 'typ' | 'status'>): boolean {
  return (
    auftrag.status === 'wartet_auf_freigabe' && AUFTRAGSTYPEN[auftrag.typ].freigabe === 'seite'
  );
}
